import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import GSAPGrid from './GSAPGrid';
import Magnetic from './ux/Magnetic';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const { FiArrowRight } = FiIcons;

const Hero = () => {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const bgRef = useRef(null);
  const badgeRef = useRef(null);
  const title1Ref = useRef(null);
  const title2Ref = useRef(null);
  const descRef = useRef(null);
  const buttonsRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline();

      // Split text logic for titles
      const splitText = (el) => {
        if (!el) return [];
        const text = el.innerText;
        el.innerHTML = '';
        return text.split('').map((char) => {
          const span = document.createElement('span');
          span.innerText = char === ' ' ? '\u00A0' : char;
          span.style.opacity = '0';
          span.style.transform = 'translateY(100%)';
          span.style.display = 'inline-block';
          el.appendChild(span);
          return span;
        });
      };

      const chars1 = splitText(title1Ref.current);
      const chars2 = splitText(title2Ref.current);

      tl.fromTo(badgeRef.current, 
        { opacity: 0, y: 20 }, 
        { opacity: 1, y: 0, duration: 0.8, ease: "power3.out" }
      )
      .to([...chars1, ...chars2], {
        opacity: 1,
        y: '0%',
        duration: 0.8,
        stagger: 0.02,
        ease: "back.out(1.2)"
      }, "-=0.4")
      .fromTo(descRef.current,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.8, ease: "power3.out" },
        "-=0.4"
      )
      .fromTo(buttonsRef.current.children,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, stagger: 0.1, ease: "power3.out" },
        "-=0.6"
      );

      gsap.to(scrollRef.current, {
        y: 10,
        repeat: -1,
        yoyo: true,
        duration: 1.5,
        ease: "power1.inOut"
      });

      // Parallax effect for Background
      gsap.to(bgRef.current, {
        y: '30%',
        ease: 'none',
        scrollTrigger: {
          trigger: heroRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: true
        }
      });
      
    }, heroRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={heroRef} className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden bg-white dark:bg-slate-950">
      <div ref={bgRef} className="absolute inset-0 w-full h-full z-0 pointer-events-none">
        <GSAPGrid />
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-cyan-100/40 dark:bg-cyan-900/20 rounded-full blur-[120px] pointer-events-none animate-pulse duration-10000"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-blue-100/40 dark:bg-blue-900/20 rounded-full blur-[120px] pointer-events-none animate-pulse duration-10000 delay-1000"></div>
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 text-left md:text-center w-full">
        <div
          ref={badgeRef}
          style={{ opacity: 0 }}
          className="inline-block mb-6 md:mb-8 px-4 py-1.5 rounded-none border border-cyan-200 dark:border-cyan-800 bg-cyan-50/50 dark:bg-cyan-900/30 backdrop-blur-sm"
        >
          <span className="text-xs font-bold tracking-[0.3em] text-cyan-600 dark:text-cyan-400 uppercase">Enterprise Software & AI</span>
        </div>

        <h1 className="text-5xl md:text-8xl font-black tracking-tighter mb-6 md:mb-8 leading-[0.9] text-slate-900 dark:text-white">
          <div ref={title1Ref} className="overflow-hidden pb-1 md:pb-2">Architecting</div>
          <div ref={title2Ref} className="text-gradient-cyan overflow-hidden pb-1 md:pb-2">Intelligence.</div>
        </h1>

        <p
          ref={descRef}
          style={{ opacity: 0 }}
          className="text-lg md:text-xl text-slate-600 dark:text-slate-400 max-w-3xl md:mx-auto mb-10 md:mb-12 leading-relaxed font-medium"
        >
          We engineer high-performance custom software and AI systems for businesses that require absolute precision and scale. From .NET enterprise cores to advanced deep learning models.
        </p>

        <div ref={buttonsRef} className="flex flex-col sm:flex-row items-stretch md:items-center justify-start md:justify-center gap-4 w-full">
          <div style={{ opacity: 0 }} className="w-full sm:w-auto">
            <Magnetic strength={0.3}>
              <button
                onClick={() => navigate('/capabilities')}
                data-cursor="hover"
                className="w-full px-10 py-5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-black uppercase tracking-widest transition-all duration-300 group flex items-center justify-center gap-2 shadow-xl shadow-slate-200 dark:shadow-none hover:scale-105"
              >
                Review My Architecture
                <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </Magnetic>
          </div>
          <div style={{ opacity: 0 }} className="w-full sm:w-auto">
            <Magnetic strength={0.3}>
              <button
                onClick={() => navigate('/ai-integration')}
                data-cursor="hover"
                className="w-full px-10 py-5 bg-white dark:bg-transparent border border-slate-200 dark:border-slate-800 hover:border-slate-900 dark:hover:border-white text-slate-900 dark:text-white font-bold uppercase tracking-widest transition-all duration-300 hover:scale-105 shadow-sm md:shadow-none"
              >
                Plan an AI Integration
              </button>
            </Magnetic>
          </div>
        </div>
      </div>

      {/* Hero Scroll Indicator */}
      <div
        ref={scrollRef}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 opacity-30"
      >
        <div className="w-[1px] h-12 bg-gradient-to-b from-cyan-400 to-transparent"></div>
      </div>
    </section>
  );
};

export default Hero;